import { InsertDriveFile } from '@mui/icons-material';
import { Box } from '@mui/material';
import { userRoles } from 'constants/constants';
import moment from 'moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Comment, commentActionEnum } from 'redux/conductInspectionSlice';
import { Project } from 'redux/projectSlice';
import { RootStates } from 'redux/store';
import './CommentItem.scss';

export type CommentItemProps = {
  comment: Comment;
  project: Project;
  component?: 'div' | 'li';
};

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  project,
  component = 'li',
}) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootStates) => state.auth.userDetails);
  const getSender = useCallback(
    (comment: Comment) => {
      return project?.team?.find(
        (teamObj) => teamObj.userId === comment.senderId,
      );
    },
    [project?.team],
  );

  const getChatBubbleClassName = () => {
    const sender = getSender(comment);

    if (comment.action === commentActionEnum.SendForApproval) {
      return 'list-group-item-warning';
    } else if (comment.action === commentActionEnum.Approve) {
      return 'list-group-item-success';
    } else if (comment.action === commentActionEnum.Disapprove) {
      return 'list-group-item-danger';
    } else if (sender?.role === userRoles.DOER) {
      return 'list-group-item-primary';
    }

    return 'list-group-item-light';
  };

  const getStatus = () => {
    if (comment.action === commentActionEnum.Approve) {
      return t('text_approved');
    } else if (comment.action === commentActionEnum.Disapprove) {
      return t('text_disapproved');
    } else if (comment.action === commentActionEnum.TaskRaised) {
      return t('text_task_raised');
    } else if (comment.action === commentActionEnum.SendForApproval) {
      return t('text_sent_for_approval');
    }

    return t('text_commented');
  };

  return (
    <Box
      className={`chat-list-item ${
        comment.senderId === user._id ? 'current-user' : ''
      } ${getChatBubbleClassName()}`}
      component={component}
    >
      <div>
        <img
          src={`https://ui-avatars.com/api/?size=24&color=fff&rounded=false&bold=true&background=random&name=${
            getSender(comment)?.name ?? ''
          }`}
          className="rounded-circle mx-1"
          alt=""
        />
        <span>{getSender(comment)?.name ?? ''}</span>
      </div>
      <span className="chat-text">{comment.comment}</span>
      {comment.documents.length > 0 && (
        <div>
          {comment.documents.map((doc) =>
            doc.docType === 'image' ? (
              <img style={{ width: '100%' }} alt="" src={doc.url} />
            ) : (
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="text-white flex-grow-1 text-decoration-none btn btn-link"
              >
                <InsertDriveFile sx={{ fontSize: 60 }} />
              </a>
            ),
          )}
        </div>
      )}
      <div className="comment-date">
        {t('text_action_date', {
          actionType: getStatus(),
          date: moment(moment(comment.commentedAt).toISOString()).format(
            'DD-MM-YYYY hh:mm A',
          ),
        })}
      </div>
    </Box>
  );
};
